import { useState } from "react";
import { PageLayout } from "@/components/PageLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Math as MathFormula } from "@/components/Math";

const commonEquations = [
  { name: "Quadratic Formula", latex: "x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}" },
  { name: "Pythagorean Theorem", latex: "a^2 + b^2 = c^2" },
  { name: "Newton's Second Law", latex: "F = ma" },
  { name: "Einstein's Mass-Energy", latex: "E = mc^2" },
  { name: "Ideal Gas Law", latex: "PV = nRT" },
  { name: "Kinetic Energy", latex: "KE = \\frac{1}{2}mv^2" },
  { name: "Wave Equation", latex: "v = f\\lambda" },
  { name: "Ohm's Law", latex: "V = IR" },
];

export default function LaTeXFormatter() {
  const [latex, setLatex] = useState("");
  const [copied, setCopied] = useState(false);

  const copyToClipboard = () => {
    navigator.clipboard.writeText(latex);
    setCopied(true);
    toast.success("Copied to clipboard!");
    setTimeout(() => setCopied(false), 2000);
  };

  const insertEquation = (equation: string) => {
    setLatex(equation);
  };

  return (
    <PageLayout calculatorName="LaTeX Equation Formatter" calculatorPath="/calculator/latex-formatter">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2">LaTeX Equation Formatter</h1>
          <p className="text-muted-foreground">
            Format mathematical equations in LaTeX
          </p>
        </div>

        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>LaTeX Equation Editor</CardTitle>
            <CardDescription>
              Write or select common equations in LaTeX format
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="latex">LaTeX Code</Label>
              <Textarea
                id="latex"
                placeholder="Enter LaTeX equation (e.g., E = mc^2)"
                value={latex}
                onChange={(e) => setLatex(e.target.value)}
                rows={4}
                className="font-mono"
              />
              <div className="flex justify-end">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={copyToClipboard}
                  disabled={!latex}
                >
                  {copied ? (
                    <>
                      <Check className="mr-2 h-4 w-4" />
                      Copied!
                    </>
                  ) : (
                    <>
                      <Copy className="mr-2 h-4 w-4" />
                      Copy LaTeX
                    </>
                  )}
                </Button>
              </div>
            </div>

            {latex && (
              <Card className="bg-gradient-to-br from-primary/10 to-primary/5 border-primary/20">
                <CardHeader>
                  <CardTitle className="text-xl">Preview</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="p-6 bg-background rounded-lg">
                    <MathFormula block>{latex}</MathFormula>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Rendered LaTeX equation
                  </p>
                </CardContent>
              </Card>
            )}

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">Common Equations</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 gap-2">
                  {commonEquations.map((eq) => (
                    <Button
                      key={eq.name}
                      variant="outline"
                      className="justify-start h-auto py-3"
                      onClick={() => insertEquation(eq.latex)}
                    >
                      <div className="text-left w-full flex items-center gap-4">
                        <div className="flex-1">
                          <div className="font-medium text-sm mb-1">{eq.name}</div>
                          <div className="font-mono text-xs text-muted-foreground">
                            {eq.latex}
                          </div>
                        </div>
                        <div className="text-right">
                          <MathFormula>{eq.latex}</MathFormula>
                        </div>
                      </div>
                    </Button>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="bg-muted/50">
              <CardHeader>
                <CardTitle className="text-lg">LaTeX Syntax Guide</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm text-muted-foreground">
                <p>• Fractions: <code className="text-foreground">\frac{"{numerator}"}{"{denominator}"}</code></p>
                <p>• Square root: <code className="text-foreground">\sqrt{"{x}"}</code></p>
                <p>• Superscript: <code className="text-foreground">x^2</code> or <code className="text-foreground">x^{"{2n}"}</code></p>
                <p>• Subscript: <code className="text-foreground">x_1</code> or <code className="text-foreground">x_{"{ab}"}</code></p>
                <p>• Greek letters: <code className="text-foreground">\alpha \beta \gamma \delta \pi</code></p>
                <p>• Summation: <code className="text-foreground">\sum_{"{i=1}"}^{"{n}"}</code></p>
                <p>• Integral: <code className="text-foreground">\int_{"{a}"}^{"{b}"}</code></p>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  );
}
